import { useState, useRef } from 'react';
import { z } from 'zod';
import {
  CheckCircle,
  Upload,
  User,
  Shield,
  Send,
  AlertCircle,
  Loader2,
  FileText,
} from 'lucide-react';
import {
  useOwnKycProfile,
  useCreateKycProfile,
  useSubmitIdDocument,
  useSubmitGuarantor,
  useRecordConsent,
  useSubmitForReview,
} from '../../hooks/useTenantOnboarding';
import { useFormValidation } from '../../hooks/useFormValidation';
import type { KycStatus } from '../../types/kyc';
import { KYC_STATUS_LABELS } from '../../types/kyc';

// ---------------------------------------------------------------------------
// Step index helpers
// ---------------------------------------------------------------------------

function currentStep(status: KycStatus): number {
  const map: Partial<Record<KycStatus, number>> = {
    PENDING: 0,
    ID_SUBMITTED: 1,
    GUARANTOR_SUBMITTED: 2,
    CONSENT_GIVEN: 3,
    UNDER_REVIEW: 4,
    APPROVED: 5,
    REJECTED: 0, // allow restart
  };
  return map[status] ?? 0;
}

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

const guarantorSchema = z.object({
  guarantorName: z.string().min(2, 'Name must be at least 2 characters'),
  guarantorEmail: z.string().email('Invalid email address'),
  guarantorPhone: z.string().min(7, 'Phone number too short'),
});

// ---------------------------------------------------------------------------
// Step components
// ---------------------------------------------------------------------------

function StepBadge({
  step,
  active,
  done,
  label,
}: {
  step: number;
  active: boolean;
  done: boolean;
  label: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
          done
            ? 'bg-green-500 text-white'
            : active
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-400'
        }`}
      >
        {done ? <CheckCircle className="h-5 w-5" /> : step + 1}
      </div>
      <span className={`text-xs font-medium ${active ? 'text-primary-700' : 'text-gray-500'}`}>
        {label}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main wizard
// ---------------------------------------------------------------------------

export default function KycOnboardingWizard() {
  const { data: profile, isLoading, error } = useOwnKycProfile();
  const createProfile = useCreateKycProfile();
  const submitId = useSubmitIdDocument();
  const submitGuarantor = useSubmitGuarantor();
  const recordConsent = useRecordConsent();
  const submitForReview = useSubmitForReview();

  // Step 1 — ID document
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

  const handleFileSelect = (file: File | undefined) => {
    setFileError(null);
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError('Only JPEG, PNG, WebP, and PDF files are accepted.');
      setSelectedFile(null);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFileError(
        `File is too large. Maximum size is 10 MB (selected: ${(file.size / 1024 / 1024).toFixed(1)} MB).`
      );
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };
  const [docType, setDocType] = useState('NATIONAL_ID');
  const fileRef = useRef<HTMLInputElement>(null);

  // Step 2 — Guarantor
  const [guarantorForm, setGuarantorForm] = useState({
    guarantorName: '',
    guarantorEmail: '',
    guarantorPhone: '',
  });
  const { errors: guarantorErrors, validate: validateGuarantor } =
    useFormValidation(guarantorSchema);

  // Step 3 — Consent
  const [consentChecked, setConsentChecked] = useState(false);

  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  // No profile yet — show create prompt
  if (
    !profile ||
    (error && (error as { response?: { status?: number } })?.response?.status === 404)
  ) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center max-w-md mx-auto">
        <Shield className="h-12 w-12 text-primary-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Start Your KYC Verification</h2>
        <p className="text-gray-500 mb-6">
          Complete identity verification to unlock full tenant access.
        </p>
        <button
          onClick={() => createProfile.mutate()}
          disabled={createProfile.isPending}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {createProfile.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Begin Verification
        </button>
      </div>
    );
  }

  // Approved
  if (profile.kycStatus === 'APPROVED') {
    return (
      <div className="bg-green-50 rounded-2xl border border-green-200 p-8 text-center max-w-md mx-auto">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Verification Complete</h2>
        <p className="text-gray-600">
          Your identity has been verified. You now have full tenant access.
        </p>
      </div>
    );
  }

  // Under review
  if (profile.kycStatus === 'UNDER_REVIEW') {
    return (
      <div className="bg-yellow-50 rounded-2xl border border-yellow-200 p-8 text-center max-w-md mx-auto">
        <Loader2 className="h-12 w-12 text-yellow-500 mx-auto mb-4 animate-spin" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Under Review</h2>
        <p className="text-gray-600">
          Your documents are being reviewed. We&apos;ll notify you once the review is complete.
        </p>
      </div>
    );
  }

  const step = currentStep(profile.kycStatus);

  const steps = [
    { label: 'ID Document', icon: FileText },
    { label: 'Guarantor', icon: User },
    { label: 'Consent', icon: Shield },
    { label: 'Submit', icon: Send },
  ];

  return (
    <div className="max-w-xl mx-auto space-y-6">
      {/* Rejection banner */}
      {profile.kycStatus === 'REJECTED' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Verification Rejected</p>
            <p className="text-sm text-red-700 mt-0.5">
              {profile.rejectionReason ?? 'Please resubmit with corrected documents.'}
            </p>
          </div>
        </div>
      )}

      {/* Progress bar */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex justify-between relative">
          <div className="absolute top-4 left-5 right-5 h-0.5 bg-gray-100 -z-10" />
          {steps.map((s, i) => (
            <StepBadge key={i} step={i} active={step === i} done={step > i} label={s.label} />
          ))}
        </div>
        <p className="text-center text-sm text-gray-500 mt-4">
          Status:{' '}
          <span className="font-medium text-gray-700">{KYC_STATUS_LABELS[profile.kycStatus]}</span>
        </p>
      </div>

      {/* Step content */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        {/* Step 0 — ID Document */}
        {step === 0 && (
          <>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary-500" /> Upload Identity Document
            </h3>
            <p className="text-sm text-gray-500">
              Upload a government-issued photo ID. Accepted: National ID, Passport, Driver&apos;s
              License.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document type</label>
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="input w-full"
              >
                <option value="NATIONAL_ID">National ID</option>
                <option value="PASSPORT">Passport</option>
                <option value="DRIVERS_LICENSE">Driver&apos;s License</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document file</label>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,.pdf,application/pdf"
                className="hidden"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => {
                  setFileError(null);
                  fileRef.current?.click();
                }}
                className={`w-full border-2 border-dashed rounded-xl p-6 text-center hover:border-primary-300 transition-colors ${fileError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}
              >
                <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  {selectedFile
                    ? selectedFile.name
                    : 'Click to select file (JPEG, PNG, WebP or PDF, max 10 MB)'}
                </p>
              </button>
              {fileError && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  {fileError}
                </p>
              )}
            </div>

            <button
              onClick={() => {
                if (!selectedFile) return;
                submitId.mutate({ file: selectedFile, docType });
              }}
              disabled={!selectedFile || submitId.isPending}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {submitId.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit ID Document
            </button>
          </>
        )}

        {/* Step 1 — Guarantor */}
        {step === 1 && (
          <>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <User className="h-5 w-5 text-primary-500" /> Guarantor Information
            </h3>
            <p className="text-sm text-gray-500">
              Provide details of a guarantor who can vouch for you.
            </p>

            {(['guarantorName', 'guarantorEmail', 'guarantorPhone'] as const).map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                  {field
                    .replace('guarantor', '')
                    .replace(/([A-Z])/g, ' $1')
                    .trim()}
                </label>
                <input
                  type={field === 'guarantorEmail' ? 'email' : 'text'}
                  value={guarantorForm[field]}
                  onChange={(e) =>
                    setGuarantorForm((prev) => ({ ...prev, [field]: e.target.value }))
                  }
                  className={`input w-full ${guarantorErrors[field] ? 'border-red-400' : ''}`}
                  placeholder={
                    field === 'guarantorName'
                      ? 'Full name'
                      : field === 'guarantorEmail'
                        ? 'Email address'
                        : 'Phone number'
                  }
                />
                {guarantorErrors[field] && (
                  <p className="text-xs text-red-500 mt-1">{guarantorErrors[field]}</p>
                )}
              </div>
            ))}

            <button
              onClick={() => {
                if (!validateGuarantor(guarantorForm)) return;
                submitGuarantor.mutate(guarantorForm);
              }}
              disabled={submitGuarantor.isPending}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {submitGuarantor.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit Guarantor Info
            </button>
          </>
        )}

        {/* Step 2 — Consent */}
        {step === 2 && (
          <>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary-500" /> NDPR Data Consent
            </h3>
            <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600 space-y-2 max-h-48 overflow-y-auto">
              <p className="font-medium text-gray-800">Nigeria Data Protection Regulation (NDPR)</p>
              <p>
                By proceeding, you consent to the collection and processing of your personal data —
                including identity documents and guarantor information — for the purpose of tenant
                verification, in accordance with the NDPR 2019 and its implementing regulations.
              </p>
              <p>
                Your data will be stored securely, used only for verification purposes, and not
                shared with third parties without your consent except as required by law.
              </p>
              <p>
                You have the right to access, correct, or request deletion of your data at any time
                by contacting your property manager.
              </p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-primary-600"
              />
              <span className="text-sm text-gray-700">
                I have read and agree to the NDPR data processing terms above.
              </span>
            </label>

            <button
              onClick={() => recordConsent.mutate()}
              disabled={!consentChecked || recordConsent.isPending}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {recordConsent.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Give Consent &amp; Continue
            </button>
          </>
        )}

        {/* Step 3 — Submit */}
        {step === 3 && (
          <>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Send className="h-5 w-5 text-primary-500" /> Submit for Review
            </h3>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" /> ID document submitted
              </div>
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" /> Guarantor information provided
              </div>
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-4 w-4" /> NDPR consent recorded
              </div>
            </div>
            <p className="text-sm text-gray-500">
              Your documents are ready for review. A property manager will verify your information
              and notify you of the outcome.
            </p>

            <button
              onClick={() => submitForReview.mutate()}
              disabled={submitForReview.isPending}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {submitForReview.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit for Review
            </button>
          </>
        )}
      </div>
    </div>
  );
}
